import { type ToastContentProps } from 'react-toastify';
import { type NotificationDto } from '../../../models/NotificationDto';

interface CustomNotificationToastProps extends ToastContentProps {
  notification: NotificationDto;
}

const getNotificationIcon = (type?: string): string => {
  switch (type?.toLowerCase()) {
    case 'success':
      return '✓';
    case 'error':
      return '✕';
    case 'warning':
      return '⚠';
    case 'info':
    default:
      return '🔔';
  }
};

const getNotificationColor = (type?: string): { bg: string; border: string; icon: string } => {
  switch (type?.toLowerCase()) {
    case 'success':
      return {
        bg: 'rgba(16, 185, 129, 0.1)',
        border: 'rgb(16, 185, 129)',
        icon: 'rgb(16, 185, 129)',
      };
    case 'error':
      return {
        bg: 'rgba(239, 68, 68, 0.1)',
        border: 'rgb(239, 68, 68)',
        icon: 'rgb(239, 68, 68)',
      };
    case 'warning':
      return {
        bg: 'rgba(245, 158, 11, 0.1)',
        border: 'rgb(245, 158, 11)',
        icon: 'rgb(245, 158, 11)',
      };
    case 'info':
    default:
      return {
        bg: 'rgba(59, 130, 246, 0.1)',
        border: 'rgb(59, 130, 246)',
        icon: 'rgb(59, 130, 246)',
      };
  }
};

const formatDate = (dateString?: string): string => {
  if (!dateString) return '';

  try {
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
};

export const EventNotificationToast = ({ notification, closeToast }: CustomNotificationToastProps) => {
  const typeValue = notification.type?.value || notification.type?.description;
  const colors = getNotificationColor(typeValue);
  const icon = getNotificationIcon(typeValue);

  return (
    <div
      style={{
        display: 'flex',
        width: '100%',
        minWidth: '320px',
        maxWidth: '420px',
        backgroundColor: '#fff',
        borderRadius: '12px',
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.12), 0 2px 6px rgba(0, 0, 0, 0.08)',
        borderLeft: `4px solid ${colors.border}`,
        overflow: 'hidden',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
    >
      {/* Icon Section */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          padding: '16px 0 16px 16px',
        }}
      >
        <div
          style={{
            width: '40px',
            height: '40px',
            borderRadius: '10px',
            backgroundColor: colors.bg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '18px',
            flexShrink: 0,
          }}
        >
          {icon}
        </div>
      </div>

      {/* Content Section */}
      <div
        style={{
          flex: 1,
          padding: '16px 12px',
          minWidth: 0,
        }}
      >
        {/* Header with Title and Close Button */}
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: '8px',
          }}
        >
          <h4
            style={{
              margin: 0,
              fontSize: '15px',
              fontWeight: 600,
              color: '#1f2937',
              lineHeight: '1.4',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {notification.title}
          </h4>
          <button
            aria-label="Fechar notificação"
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '2px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '4px',
              color: '#9ca3af',
              transition: 'all 0.15s ease',
              flexShrink: 0,
            }}
            type="button"
            onClick={() => closeToast()}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f3f4f6';
              e.currentTarget.style.color = '#6b7280';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = '#9ca3af';
            }}
          >
            <svg
              fill="none"
              height="16"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              width="16"
            >
              <line x1="18" x2="6" y1="6" y2="18" />
              <line x1="6" x2="18" y1="6" y2="18" />
            </svg>
          </button>
        </div>

        {/* Description */}
        {notification.description ? (
          <p
            style={{
              margin: '6px 0 0 0',
              fontSize: '13px',
              color: '#6b7280',
              lineHeight: '1.5',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {notification.description}
          </p>
        ) : null}

        {/* Footer with Type Badge and Timestamp */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginTop: '12px',
            gap: '8px',
          }}
        >
          {/* Timestamp */}
          {notification.registration_date ? (
            <span
              style={{
                fontSize: '11px',
                color: '#9ca3af',
                whiteSpace: 'nowrap',
              }}
            >
              {formatDate(notification.registration_date)}
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default EventNotificationToast;
